<div class="dashboard-container" *ngIf="hasDashboardAccess">

  <!-- ═══════ Welcome Header ═══════ -->
  <div class="dashboard-header">
    <div class="header-content">
      <div class="greeting">
        <h1>Welcome back<span *ngIf="userName">, {{ userName }}</span></h1>
        <p class="subtitle">Here's your dental lab overview for today</p>
      </div>
      <div class="header-date">
        <mat-icon>calendar_today</mat-icon>
        {{ today | date:'EEEE, MMMM d, yyyy' }}
      </div>
    </div>
  </div>

  <!-- ═══════ Quick Actions ═══════ -->
  <div class="quick-actions">
    <button class="action-btn primary" (click)="navigate('/joborder/new')">
      <mat-icon>add_circle</mat-icon>
      <span>New Job Order</span>
    </button>
    <button class="action-btn secondary" (click)="navigate('/challan/new')">
      <mat-icon>local_shipping</mat-icon>
      <span>New Challan</span>
    </button>
    <button class="action-btn tertiary" (click)="navigate('/doctor')">
      <mat-icon>person_search</mat-icon>
      <span>Doctors</span>
    </button>
    <button class="action-btn outline" (click)="navigate('/invoice')">
      <mat-icon>receipt_long</mat-icon>
      <span>Invoices</span>
    </button>
  </div>

  <!-- ═══════ Loading skeleton ═══════ -->
  <div class="skeleton-grid" *ngIf="loading">
    <div class="skeleton-card" *ngFor="let i of [1,2,3,4]"></div>
  </div>

  <!-- ═══════ Primary Stat Cards ═══════ -->
  <div class="stats-grid" *ngIf="!loading">
    <div class="stat-card animate-in" (click)="navigate('/doctor')">
      <div class="stat-icon doctors"><mat-icon>person</mat-icon></div>
      <div class="stat-content">
        <span class="stat-value">{{ doctorCount }}</span>
        <span class="stat-label">Doctors</span>
      </div>
    </div>
    <div class="stat-card animate-in" (click)="navigate('/product')">
      <div class="stat-icon products"><mat-icon>inventory_2</mat-icon></div>
      <div class="stat-content">
        <span class="stat-value">{{ productCount }}</span>
        <span class="stat-label">Products</span>
      </div>
    </div>
    <div class="stat-card animate-in">
      <div class="stat-icon users"><mat-icon>group</mat-icon></div>
      <div class="stat-content">
        <span class="stat-value">{{ userCount }}</span>
        <span class="stat-label">Users</span>
      </div>
    </div>
    <div class="stat-card animate-in" (click)="navigate('/employee')">
      <div class="stat-icon employees"><mat-icon>badge</mat-icon></div>
      <div class="stat-content">
        <span class="stat-value">{{ employeeCount }}</span>
        <span class="stat-label">Employees</span>
      </div>
    </div>
  </div>

  <!-- ═══════ Job Status Cards + Revenue ═══════ -->
  <div class="stats-grid five-col" *ngIf="!loading">
    <div class="stat-card animate-in accent">
      <div class="stat-icon production"><mat-icon>precision_manufacturing</mat-icon></div>
      <div class="stat-content">
        <span class="stat-value">{{ inProductionJobsCount }}</span>
        <span class="stat-label">In Production</span>
      </div>
    </div>
    <div class="stat-card animate-in accent">
      <div class="stat-icon hold"><mat-icon>pause_circle</mat-icon></div>
      <div class="stat-content">
        <span class="stat-value">{{ holdJobsCount }}</span>
        <span class="stat-label">On Hold</span>
      </div>
    </div>
    <div class="stat-card animate-in accent">
      <div class="stat-icon repeat"><mat-icon>replay</mat-icon></div>
      <div class="stat-content">
        <span class="stat-value">{{ repeatJobsCount }}</span>
        <span class="stat-label">Repeated</span>
      </div>
    </div>
    <div class="stat-card animate-in accent">
      <div class="stat-icon repair"><mat-icon>build</mat-icon></div>
      <div class="stat-content">
        <span class="stat-value">{{ repairJobsCount }}</span>
        <span class="stat-label">Repaired</span>
      </div>
    </div>
    <div class="stat-card animate-in revenue-card">
      <div class="stat-icon revenue"><mat-icon>currency_rupee</mat-icon></div>
      <div class="stat-content">
        <span class="stat-value">{{ monthlyRevenue | number:'1.0-0' }}</span>
        <span class="stat-label">Revenue (This Month)</span>
      </div>
    </div>
  </div>

  <!-- ═══════ Charts + Top Doctors Section ═══════ -->
  <div class="charts-section two-col" *ngIf="!loading">
    <!-- Top Products -->
    <div class="chart-card" *ngIf="topProducts.length">
      <div class="chart-card-header">
        <h3>Top Products</h3>
        <span class="chart-subtitle">This month by usage (done jobs)</span>
        <a class="view-more-link" (click)="viewAllTopProducts()">View More</a>
      </div>
      <div class="chart-body pie-body">
        <div class="pie-canvas-wrap">
          <canvas baseChart
            [datasets]="productPieDatasets"
            [labels]="productPieLabels"
            [options]="productPieOptions"
            [type]="'pie'">
          </canvas>
        </div>
        <ul class="pie-legend-list" [class.compact-legend]="productLegendItems.length > 6">
          <li class="pie-legend-item" *ngFor="let item of productLegendItems">
            <span class="pie-legend-dot" [style.background-color]="item.color"></span>
            <span class="pie-legend-text">{{ item.name }} - {{ item.units }} units ({{ item.percentage }}%)</span>
          </li>
        </ul>
      </div>
    </div>

    <!-- Top Doctors -->
    <div class="chart-card top-doctors-card" *ngIf="topDoctors.length">
      <div class="chart-card-header">
        <h3>Top Doctors</h3>
        <span class="chart-subtitle">This month by business</span>
        <a class="view-more-link" (click)="viewAllTopDoctors()">View More</a>
      </div>
      <div class="top-doctors-list">
        <div class="top-doc-header">
          <span class="top-doc-header-name">Doctor Name</span>
          <span class="top-doc-header-business">Business Amount</span>
          <span class="top-doc-header-cases">Cases Completed</span>
        </div>
        <div class="top-doc-row" *ngFor="let doc of topDoctors; let i = index">
          <div class="doc-name-cell">
            <span class="doc-rank">{{ i + 1 }}</span>
            <span class="doc-name">{{ doc.name }}</span>
          </div>
          <span class="doc-business">₹{{ doc.business_amount | number:'1.0-0' }}</span>
          <span class="doc-cases">{{ doc.completed_cases }}</span>
        </div>
      </div>
    </div>
  </div>

  <!-- ═══════ Monthly Job Trend (own row) ═══════ -->
  <div class="charts-section single-col" *ngIf="!loading">
    <div class="chart-card">
      <div class="chart-card-header">
        <h3>Monthly Job Trend</h3>
        <span class="chart-subtitle">Last 6 months</span>
      </div>
      <div class="chart-body trend-body">
        <canvas baseChart
          [datasets]="barDatasets"
          [labels]="barLabels"
          [options]="barOptions"
          [type]="'bar'">
        </canvas>
      </div>
    </div>
  </div>

  <!-- ═══════ Delivery Status ═══════ -->
  <div class="delivery-section" *ngIf="!loading">
    <h2><mat-icon>local_shipping</mat-icon> Delivery Status</h2>
    <div class="delivery-grid">
      <!-- Late Deliveries -->
      <div class="delivery-card late-border">
        <div class="delivery-card-header late delivery-card-header-filterable">
          <div class="delivery-card-header-title">
            <mat-icon>warning</mat-icon>
            <h3>Late Deliveries ({{ selectedLateDeliveryWindowLabel }})</h3>
          </div>
          <div class="delivery-card-header-actions">
            <span class="badge" *ngIf="lateDeliveries.length">{{ lateDeliveries.length }}</span>
            <select
              class="delivery-window-select"
              [(ngModel)]="selectedLateDeliveryWindow"
              (ngModelChange)="onLateDeliveryWindowChange()"
              aria-label="Late delivery window">
              <option *ngFor="let option of lateDeliveryWindowOptions" [ngValue]="option.value">
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>
        <div class="delivery-card-body">
          <table *ngIf="lateDeliveries.length">
            <thead>
              <tr><th>Job No</th><th>Doctor</th><th>Date</th><th>Status</th></tr>
            </thead>
            <tbody>
              <tr *ngFor="let item of lateDeliveries">
                <td class="job-no-cell">{{ item.job_no }}</td>
                <td>{{ item.doctor_name }}</td>
                <td>{{ item.delivery_date }}</td>
                <td><span class="status-pill late">{{ item.delivery_due }}</span></td>
              </tr>
            </tbody>
          </table>
          <div class="empty-state" *ngIf="!lateDeliveries.length">
            <mat-icon>check_circle</mat-icon>
            <p>{{ lateDeliveryEmptyStateLabel }}</p>
          </div>
        </div>
      </div>

      <!-- Today's Deliveries -->
      <div class="delivery-card today-border">
        <div class="delivery-card-header today">
          <mat-icon>today</mat-icon>
          <h3>Today's Deliveries</h3>
          <span class="badge" *ngIf="todayDeliveries.length">{{ todayDeliveries.length }}</span>
        </div>
        <div class="delivery-card-body">
          <table *ngIf="todayDeliveries.length">
            <thead>
              <tr><th>Job No</th><th>Doctor</th><th>Date</th><th>Status</th></tr>
            </thead>
            <tbody>
              <tr *ngFor="let item of todayDeliveries">
                <td class="job-no-cell">{{ item.job_no }}</td>
                <td>{{ item.doctor_name }}</td>
                <td>{{ item.delivery_date }}</td>
                <td><span class="status-pill today">{{ item.delivery_due }}</span></td>
              </tr>
            </tbody>
          </table>
          <div class="empty-state" *ngIf="!todayDeliveries.length">
            <mat-icon>event_available</mat-icon>
            <p>No deliveries today</p>
          </div>
        </div>
      </div>

      <!-- Upcoming Deliveries -->
      <div class="delivery-card upcoming-border">
        <div class="delivery-card-header upcoming">
          <mat-icon>upcoming</mat-icon>
          <h3>Upcoming Deliveries</h3>
          <span class="badge" *ngIf="upcomingDeliveries.length">{{ upcomingDeliveries.length }}</span>
        </div>
        <div class="delivery-card-body">
          <table *ngIf="upcomingDeliveries.length">
            <thead>
              <tr><th>Job No</th><th>Doctor</th><th>Date</th><th>Status</th></tr>
            </thead>
            <tbody>
              <tr *ngFor="let item of upcomingDeliveries">
                <td class="job-no-cell">{{ item.job_no }}</td>
                <td>{{ item.doctor_name }}</td>
                <td>{{ item.delivery_date }}</td>
                <td><span class="status-pill upcoming">{{ item.delivery_due }}</span></td>
              </tr>
            </tbody>
          </table>
          <div class="empty-state" *ngIf="!upcomingDeliveries.length">
            <mat-icon>event</mat-icon>
            <p>No upcoming deliveries</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- No access message -->
<div class="no-access" *ngIf="!hasDashboardAccess">
  <mat-icon>lock</mat-icon>
  <h2>Dashboard Access Restricted</h2>
  <p>You don't have permission to view the dashboard.</p>
</div>
